import {
  canOwnIncidentWork,
  canOwnSafeguardingWork
} from '@features/organisation/utils/roleGroups'
import { FollowUpActionStatusEnum } from '@lmc/validations'
import { Form as AntdForm, DatePicker, Input, Select } from 'antd'
import type { Dayjs } from 'dayjs'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import AttachmentsField from '../../attachments/AttachmentsField'
import {
  type AttachmentsValue,
  NO_ATTACHMENTS
} from '../../attachments/caseAttachments'
import StepDrawer from '../../StepDrawer'
import StepField from '../../StepField'
import { DATE_FORMAT } from '../../stepLayout'
import { useTeamMemberOptions } from '../../stepOptions'
import { fromDateOnlyIso, toDateOnlyIso } from '../../stepValues'
import {
  completedAtFor,
  type FollowUpActionValue
} from './followUpActionValues'

export const STATUS_LABEL_KEYS: Record<FollowUpActionStatusEnum, string> = {
  [FollowUpActionStatusEnum.completed]:
    incidentReportsConstants.CASE_V2_ACTIONS_STATUS_COMPLETED,
  [FollowUpActionStatusEnum.notStarted]:
    incidentReportsConstants.CASE_V2_ACTIONS_STATUS_NOT_STARTED,
  [FollowUpActionStatusEnum.pending]:
    incidentReportsConstants.CASE_V2_ACTIONS_STATUS_PENDING,
  [FollowUpActionStatusEnum.skipped]:
    incidentReportsConstants.CASE_V2_ACTIONS_STATUS_SKIPPED
}

type ActionFormValues = {
  assignedUserId?: string | null
  attachments: AttachmentsValue
  details?: string
  dueDate?: Dayjs | null
  status: FollowUpActionStatusEnum
  title?: string
}

type FollowUpActionDrawerProps = {
  /** The action being edited, or the prefill for a new one. */
  action: FollowUpActionValue
  isNew: boolean
  /** Owners are then the home's safeguarding officers. */
  isSafeguarding: boolean
  onClose: () => void
  onSubmit: (action: FollowUpActionValue) => void
  testId: string
}

/** Adds or edits one action. */
const FollowUpActionDrawer = ({
  action,
  isNew,
  isSafeguarding,
  onClose,
  onSubmit,
  testId
}: FollowUpActionDrawerProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const [form] = AntdForm.useForm<ActionFormValues>()
  // Per the design, only senior carers, admin and management own an action;
  // one already saved to someone else stays named.
  const ownerOptions = useTeamMemberOptions({
    filter: isSafeguarding ? canOwnSafeguardingWork : canOwnIncidentWork,
    saved: [action.assignedUserId]
  })
  const statusOptions = Object.values(FollowUpActionStatusEnum).map(
    (status) => ({ label: t(STATUS_LABEL_KEYS[status]), value: status })
  )

  const submit = (values: ActionFormValues) =>
    onSubmit({
      ...(action.id ? { id: action.id } : {}),
      assignedUserId: values.assignedUserId ?? null,
      attachments: values.attachments,
      completedAt: completedAtFor(values.status, isNew ? undefined : action),
      details: values.details?.trim() ?? '',
      dueDate: toDateOnlyIso(values.dueDate),
      status: values.status,
      title: values.title?.trim() ?? ''
    })

  return (
    <StepDrawer
      form={form}
      initialValues={{
        assignedUserId: action.assignedUserId ?? undefined,
        attachments: action.attachments ?? NO_ATTACHMENTS,
        details: action.details,
        dueDate: fromDateOnlyIso(action.dueDate),
        status: action.status,
        title: action.title
      }}
      onCancel={onClose}
      onConfirm={submit}
      title={t(
        isNew
          ? incidentReportsConstants.CASE_V2_ACTIONS_ADDING_TITLE
          : incidentReportsConstants.CASE_V2_ACTIONS_EDITING_TITLE
      )}
      confirmLabel={t(
        isNew
          ? incidentReportsConstants.CASE_V2_ACTIONS_ADD
          : incidentReportsConstants.CASE_V2_SAVE
      )}
      testId={testId}
    >
      <StepField
        name="title"
        isRequired
        label={t(incidentReportsConstants.CASE_V2_ACTIONS_TITLE)}
      >
        <Input data-testid={`${testId}-title`} />
      </StepField>
      <StepField
        name="details"
        label={t(incidentReportsConstants.CASE_V2_ACTIONS_DETAILS)}
      >
        <Input.TextArea
          data-testid={`${testId}-details`}
          autoSize={{ minRows: 3 }}
        />
      </StepField>
      <StepField
        name="assignedUserId"
        isRequired
        label={t(incidentReportsConstants.CASE_V2_ACTIONS_OWNER_FIELD)}
        hint={t(incidentReportsConstants.CASE_V2_ACTIONS_OWNER_HINT)}
      >
        <Select
          data-testid={`${testId}-owner`}
          optionFilterProp="label"
          options={ownerOptions}
          placeholder={t(incidentReportsConstants.CASE_V2_SELECT_PLACEHOLDER)}
          showSearch
        />
      </StepField>
      <StepField
        name="dueDate"
        label={t(incidentReportsConstants.CASE_V2_ACTIONS_DUE_DATE)}
        hint={t(incidentReportsConstants.CASE_V2_ACTIONS_DUE_DATE_HINT)}
      >
        <DatePicker
          data-testid={`${testId}-due-date`}
          format={DATE_FORMAT}
          placeholder={t(
            incidentReportsConstants.CASE_V2_ACTIONS_DUE_DATE_PLACEHOLDER
          )}
        />
      </StepField>
      <StepField
        name="status"
        label={t(incidentReportsConstants.CASE_V2_ACTIONS_STATUS)}
      >
        <Select data-testid={`${testId}-status`} options={statusOptions} />
      </StepField>
      <StepField
        name="attachments"
        label={t(incidentReportsConstants.CASE_V2_ATTACHMENTS)}
        hint={t(incidentReportsConstants.CASE_V2_ATTACHMENTS_HINT)}
      >
        <AttachmentsField testId={`${testId}-attachments`} />
      </StepField>
    </StepDrawer>
  )
}

export default FollowUpActionDrawer
